import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";

const FAQS = [
  {
    q: "What actually happens each day?",
    a: "In the morning you name one move for a business. In the evening you close it out — did it happen, and what came of it. That's the whole loop. It takes under a minute per business.",
  },
  {
    q: "Is the PIN real security?",
    a: "Yes. The PIN is hashed with PBKDF2, 100,000 iterations and a unique salt — never stored in plain text. After five wrong guesses the app pauses for five minutes, and every guess is slowed on the server to resist brute-force attacks. Your data also sits behind account authentication and row-level access rules, so nobody else's account can read it.",
  },
  {
    q: "What if I miss a day?",
    a: "Nothing happens. There are no streaks, no red marks, no guilt copy. Missing a day is information, not a failure — you'll see it in the history line and read it as a pattern.",
  },
  {
    q: "How many businesses can I track?",
    a: "Commitment includes one business. Operator and Vault include unlimited businesses. Each business gets fully separate tasks, journal, phonebook and history — nothing bleeds across.",
  },
  {
    q: "What is the monthly bold move?",
    a: "On the 1st of each month, each business asks for one bold move — the thing that's bigger than a daily task. It's stored separately and tagged as a monthly risk so you can look back at what you were willing to bet on.",
  },
  {
    q: "Can I get my data out?",
    a: "Yes. Your record is yours; export is available and we never sell or share it with third parties.",
  },
  {
    q: "Does it work on my phone?",
    a: "Yes. The Founders App is built mobile-first — business chips at the top, tabs at the bottom, same loop as desktop. Add it to your home screen and it opens full-screen like a native app.",
  },
  {
    q: "Are there native apps coming?",
    a: "Works in any browser today. Additional platforms may come later. Everything syncs through the same account, so whatever you write today carries straight over if and when they land.",
  },
  {
    q: "How is this different from Notion or a to-do app?",
    a: "A to-do app measures intention — how much you planned. This measures what actually happened: one move in the morning, one honest outcome in the evening, per business. Notion can be shaped into anything, which is exactly the problem; most founders spend the first week building the system and the second week abandoning it. This has one opinionated loop, a PIN lock, and separate records per business. It does less on purpose.",
  },
  {
    q: "What happens if I stop using it for a few weeks?",
    a: "Nothing breaks and nothing scolds you. There are no streaks to lose and no catch-up screens. Your record sits exactly where you left it, and the gap simply shows in the timeline as what it is — a stretch where the business needed you elsewhere. Come back, read the last few entries, and write today's move.",
  },
  {
    q: "Can I delete my account?",
    a: "Any time, from Settings inside the app. You type DELETE to confirm, and your sign-in plus every business, entry, bold move, journal page and contact is deleted from the live database within 30 days, and from encrypted backups within 90 days. Export your data first if you want a record.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ — how The Founders App works" },
      {
        name: "description",
        content:
          "Answers on the daily loop, the four-digit lock, multiple businesses, monthly bold moves, missed days and data export in The Founders App.",
      },
      { property: "og:title", content: "FAQ — how The Founders App works" },
      {
        property: "og:description",
        content: "The daily loop, the lock, multi-business support and what happens if you skip a day.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/faq" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          url: "https://thefoundersapp.com/faq",
          name: "FAQ — how The Founders App works",
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "[data-answer]"],
          },
        }),
      },
    ],

  }),
  component: Faq,
});

function Faq() {
  useReveal();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-4xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Questions</p>
        <h1 className="reveal etched mt-5 text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          Straight answers.
        </h1>

        <div className="mt-14 space-y-3">
          {FAQS.map((item, i) => (
            <details
              key={item.q}
              className="reveal group soft-panel px-6 py-5 transition-colors duration-300 hover:bg-accent/30"
              style={{ ["--d" as string]: `${140 + i * 70}ms` }}
            >
              <summary className="flex cursor-pointer list-none items-baseline gap-5">
                <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/60 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 text-base font-medium">{item.q}</span>
                <span className="text-muted-foreground transition-transform duration-300 group-open:rotate-45">
                  +
                </span>
              </summary>
              <p data-answer className="mt-4 pl-10 text-sm leading-relaxed text-muted-foreground">
                {item.a}
              </p>

            </details>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
